import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ClipboardCheck, RefreshCw } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { read, command, clientPath, type Page } from '@/services/vault/vault.http'
import {
  reviewsPath,
  reviewPath,
  reviewReasons,
  serviceLabels,
  categoryLabels,
  categoriesFor,
  exactFinancialYear,
  isUuid,
  validInspection,
  validAliases,
  validSources,
  SOURCE_REVIEW_TERMS,
  type SourceAliasChoice,
  type SourceReviewSelection,
  type SourceReviewStatus,
  type SourceReviewItem,
  type SourceReviewInspection,
  type ReviewAction,
  type SourceDocumentsPage,
  type SourceDocumentChoice,
} from '@/services/vault/sourceReviews'
import { CommandStatus, Field, useCommand, useStepUp } from './VaultForms'
import SourceAdoptionPanel from './SourceAdoptionPanel'

export default function SourceReviewsPanel({
  client,
  owner,
  accountKey,
}: {
  client: string
  owner: boolean
  accountKey: unknown[]
}) {
  const { user } = useAuth(),
    queryClient = useQueryClient()
  const permissions = user?.permissions ?? []
  const independentOwner =
    owner &&
    user?.authScope === 'client_vault' &&
    ['individual_client', 'business_owner_client'].includes(user?.role ?? '')
  const firm =
    user?.authScope === 'user' &&
    ['firm_owner', 'firm_admin', 'ca_partner'].includes(user?.role ?? '') &&
    permissions.includes('txh.document.read')
  const allowed = independentOwner || firm
  const canPropose =
    firm &&
    permissions.includes('txh.client.read') &&
    (permissions.includes('txh.vault.upload') || permissions.includes('txh.vault.publish'))
  const [page, setPage] = useState(1),
    [selected, setSelected] = useState(''),
    [ack, setAck] = useState<string | null>(null)
  const [code, setCode] = useState(''),
    [proposalGeneration, setProposalGeneration] = useState(0)
  const actions = useCommand(),
    stepUp = useStepUp()
  const authority = JSON.stringify([
    accountKey,
    client,
    user?.id,
    user?.currentSessionId,
    user?.authScope,
    user?.role,
    permissions,
  ])
  const reviewKey = [...accountKey, client, 'source-reviews']
  const inventory = useQuery({
    queryKey: [...reviewKey, 'page', page, authority],
    queryFn: () => read<Page<SourceReviewItem>>(reviewsPath(client), { page, page_size: 20 }),
    enabled: allowed,
    staleTime: 0,
  })
  const detail = useQuery({
    queryKey: [...reviewKey, 'inspection', selected, authority],
    queryFn: () => read<SourceReviewInspection>(reviewPath(client, selected)),
    enabled: allowed && !!selected,
    staleTime: 0,
  })
  const aliases = useQuery({
    queryKey: [...accountKey, client, 'source-review-aliases', authority],
    queryFn: async () => {
      const value = await read<{ items: SourceAliasChoice[] }>(
        `${clientPath(client)}/source-aliases`
      )
      if (!validAliases(value))
        throw new Error('Current originating client records could not be verified')
      return value
    },
    enabled: canPropose,
    staleTime: 0,
  })
  const view =
    !inventory.isFetching &&
    !inventory.isError &&
    !detail.isFetching &&
    !detail.isError &&
    validInspection(detail.data, client, selected)
      ? detail.data
      : undefined
  const binding = view
    ? JSON.stringify([authority, client, view, detail.dataUpdatedAt, inventory.dataUpdatedAt])
    : null
  const refresh = async () => {
    setAck(null)
    await queryClient.invalidateQueries({ queryKey: reviewKey })
  }
  const refreshCopy = async () => {
    setAck(null)
    await queryClient.invalidateQueries({ queryKey: [...accountKey, client] })
  }
  const decide = (action: ReviewAction) => {
    if (
      !view ||
      actions.busy ||
      (action === 'approve' && ack !== binding) ||
      (action === 'cancel' ? !firm : !independentOwner)
    )
      return
    const payload = {
      action,
      approve_reconciliation: action === 'approve',
      selection_sha256: view.selection_sha256,
      source_snapshot_sha256: view.source_snapshot_sha256,
      terms_version: SOURCE_REVIEW_TERMS,
    }
    void actions.run(async () => {
      // The POST may be an exact historical receipt. Current status comes only
      // from a new authorized GET, never from that receipt's earlier state.
      await command(reviewPath(client, selected) + '/decisions', {
        ...payload,
        idempotency_key: actions.idempotencyKey({ client, review: selected, ...payload }),
      })
      await refresh()
    })
  }
  if (!allowed) return <p role="alert">Current source-review authority is required.</p>
  return (
    <div className="min-w-0 space-y-4 text-text-primary [&_button]:max-w-full [&_button]:whitespace-normal">
      <section className="card card-elevated p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-semibold flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5" />
            Document ownership reviews
          </h2>
          <button
            className="btn btn-secondary"
            disabled={actions.busy || inventory.isFetching || detail.isFetching}
            onClick={() => void refresh()}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh reviews
          </button>
        </div>
        <p className="text-sm text-text-secondary">
          Review the proposed interpretation of an existing firm document. Approval records that
          interpretation; copying into the client vault requires separate authorization.
        </p>
        {independentOwner && (
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={event => {
              event.preventDefault()
              if (!code.trim() || actions.busy) return
              setAck(null)
              const originalCode = code
              setCode('')
              void actions.run(async () => {
                await stepUp(originalCode)
                await refresh()
              })
            }}
          >
            <Field
              label="Fresh authenticator or backup code"
              type="password"
              autoComplete="one-time-code"
              value={code}
              onChange={event => setCode(event.target.value)}
              required
            />
            <button className="btn btn-secondary" disabled={actions.busy || !code.trim()}>
              Verify identity for reviews
            </button>
            <p className="text-xs text-text-secondary w-full">
              Fresh verification is required to inspect or decide on reviews.
            </p>
          </form>
        )}
        {inventory.isFetching && <p role="status">Checking current review access…</p>}
        {inventory.isError && <p role="alert">{inventory.error.message}</p>}
        {!inventory.isFetching && !inventory.isError && inventory.data && (
          <>
            {inventory.data.items.length === 0 && (
              <p className="text-sm text-text-secondary">No document reviews on this page.</p>
            )}
            <div className="space-y-3">
              {inventory.data.items.map(row => (
                <article
                  key={row.review_id}
                  className="border border-surface-overlay rounded-lg p-4 flex flex-wrap items-center justify-between gap-3"
                >
                  <div>
                    <h3 className="font-medium">{categoryLabels[row.category] ?? row.category}</h3>
                    <p className="text-sm text-text-secondary">
                      {serviceLabels[row.service]} · {row.financial_year ?? 'Undated'} ·{' '}
                      <span className="capitalize">{row.state}</span>
                    </p>
                  </div>
                  <button
                    className="btn btn-secondary"
                    disabled={actions.busy}
                    onClick={() => {
                      setAck(null)
                      setSelected(row.review_id)
                    }}
                  >
                    Inspect review
                  </button>
                </article>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <button
                className="btn btn-secondary"
                disabled={page === 1 || actions.busy}
                onClick={() => {
                  setAck(null)
                  setSelected('')
                  setPage(page - 1)
                }}
              >
                Previous reviews
              </button>
              <p>
                Page {page} · {inventory.data.total} authorized reviews
              </p>
              <button
                className="btn btn-secondary"
                disabled={page * inventory.data.page_size >= inventory.data.total || actions.busy}
                onClick={() => {
                  setAck(null)
                  setSelected('')
                  setPage(page + 1)
                }}
              >
                Next reviews
              </button>
            </div>
          </>
        )}
      </section>
      {selected && (
        <section className="card card-elevated p-5 space-y-4" aria-label="Selected document review">
          {detail.isFetching && <p role="status">Inspecting the current proposal…</p>}
          {detail.isError && <p role="alert">{detail.error.message}</p>}
          {!inventory.isFetching &&
            !inventory.isError &&
            !detail.isFetching &&
            !detail.isError &&
            detail.data &&
            !view && (
              <p role="alert">The exact review details could not be verified. Refresh reviews.</p>
            )}
          {view && (
            <>
              <h2 className="font-semibold">Proposed document interpretation</h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                {[
                  [
                    'Client record',
                    `${view.selection.customer_type === 'individual' ? 'Individual' : 'Business'} client record #${view.selection.customer_id}`,
                  ],
                  [
                    'Ownership',
                    view.selection.classification === 'client_controlled'
                      ? 'Client-owned document'
                      : 'Verified professional output',
                  ],
                  ['Service', serviceLabels[view.selection.service]],
                  ['Category', categoryLabels[view.selection.category]],
                  [
                    'Financial year',
                    view.selection.financial_year ?? 'Undated — no financial year assigned',
                  ],
                  ['Reason', reviewReasons[view.selection.reason]],
                  ['Current review status', view.state],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-text-secondary">{label}</dt>
                    <dd className="font-medium capitalize">{value}</dd>
                  </div>
                ))}
              </dl>
              <p className="text-sm text-text-secondary">
                The proposing firm confirmed that this source is nonprivate and belongs to the
                client.
              </p>
              {independentOwner && view.state === 'pending' && (
                <label className="flex items-start gap-2 text-sm text-text-secondary">
                  <input
                    type="checkbox"
                    checked={ack === binding}
                    disabled={actions.busy}
                    onChange={event => setAck(event.target.checked ? binding : null)}
                  />
                  I have reviewed this exact interpretation and approve it.
                </label>
              )}
              <div className="flex flex-wrap gap-3">
                {independentOwner && view.state === 'pending' && (
                  <>
                    <button
                      className="btn btn-primary"
                      disabled={actions.busy || ack !== binding}
                      onClick={() => decide('approve')}
                    >
                      Approve interpretation
                    </button>
                    <button
                      className="btn btn-secondary"
                      disabled={actions.busy}
                      onClick={() => decide('reject')}
                    >
                      Reject interpretation
                    </button>
                  </>
                )}
                {independentOwner && view.state === 'approved' && (
                  <button
                    className="btn btn-secondary"
                    disabled={actions.busy}
                    onClick={() => decide('revoke')}
                  >
                    Revoke interpretation approval
                  </button>
                )}
                {firm && ['pending', 'approved'].includes(view.state) && (
                  <button
                    className="btn btn-secondary"
                    disabled={actions.busy}
                    onClick={() => decide('cancel')}
                  >
                    Cancel originating firm proposal
                  </button>
                )}
              </div>
              {!['pending', 'approved'].includes(view.state) && (
                <p className="text-sm text-text-secondary">
                  This review is closed. A new interpretation requires a new proposal.
                </p>
              )}
            </>
          )}
        </section>
      )}
      {firm && (
        <SourceAdoptionPanel
          key={authority}
          client={client}
          authority={authority}
          reviewKey={reviewKey}
          view={view}
          inspectionRevision={binding}
          permitted={
            !!view &&
            permissions.includes('txh.client.read') &&
            permissions.includes(
              view.selection.classification === 'client_output'
                ? 'txh.vault.publish'
                : 'txh.vault.upload'
            )
          }
          refresh={refreshCopy}
        />
      )}
      <CommandStatus {...actions} />
      {canPropose && (
        <section className="card card-elevated p-5 space-y-4">
          <h2 className="font-semibold">Propose an interpretation</h2>
          {aliases.isError && <p role="alert">{aliases.error.message}</p>}
          {aliases.isFetching && <p role="status">Checking current originating client records…</p>}
          <SourceProposalForm
            key={authority + proposalGeneration}
            client={client}
            authority={authority}
            accountKey={accountKey}
            aliases={aliases.data?.items ?? []}
            ready={!aliases.isFetching && !aliases.isError && !!aliases.data}
            aliasesRevision={aliases.dataUpdatedAt}
            upload={permissions.includes('txh.vault.upload')}
            publish={permissions.includes('txh.vault.publish')}
            onSaved={async review => {
              setAck(null)
              setSelected(review.review_id)
              setPage(1)
              setProposalGeneration(value => value + 1)
              await refresh()
            }}
          />
        </section>
      )}
    </div>
  )
}

function SourceProposalForm({
  client,
  authority,
  accountKey,
  aliases,
  ready,
  aliasesRevision,
  upload,
  publish,
  onSaved,
}: {
  client: string
  authority: string
  accountKey: unknown[]
  aliases: SourceAliasChoice[]
  ready: boolean
  aliasesRevision: number
  upload: boolean
  publish: boolean
  onSaved: (review: SourceReviewStatus) => Promise<void>
}) {
  const [draft, setDraft] = useState({
    source: '',
    alias: '',
    classification: '',
    service: '',
    category: '',
    period: '',
    year: '',
    reason: '',
  })
  const [nonprivate, setNonprivate] = useState<string | null>(null),
    [owned, setOwned] = useState<string | null>(null)
  const [touched, setTouched] = useState(false),
    actions = useCommand(),
    inFlight = useRef(false)
  const [sourcePage, setSourcePage] = useState(1)
  const queryClient = useQueryClient()
  const sources = useQuery({
    queryKey: [...accountKey, client, 'source-review-sources', sourcePage, authority],
    queryFn: async () => {
      const value = await read<SourceDocumentsPage>(reviewsPath(client) + '/sources', {
        page: sourcePage,
        page_size: 20,
      })
      if (!validSources(value, client, sourcePage))
        throw new Error('Current original document choices could not be verified')
      return value
    },
    enabled: ready,
    staleTime: 0,
  })
  const sourcesReady = ready && !sources.isFetching && !sources.isError && !!sources.data
  const picked: SourceDocumentChoice | undefined = sourcesReady
    ? sources.data?.items.find(item => item.source_document_id === draft.source && item.reviewable)
    : undefined
  useEffect(() => {
    setNonprivate(null)
    setOwned(null)
  }, [ready, aliasesRevision, sources.isFetching, sources.isError, sources.dataUpdatedAt])
  const update = (value: Partial<typeof draft>) => {
    setDraft(current => ({ ...current, ...value }))
    setNonprivate(null)
    setOwned(null)
  }
  const binding = JSON.stringify([
    authority,
    client,
    draft,
    aliasesRevision,
    sources.dataUpdatedAt,
    picked,
  ])
  const alias = ready ? aliases.find(row => row.source_alias_id === draft.alias) : undefined
  const permitted =
    draft.classification === 'client_output'
      ? publish
      : draft.classification === 'client_controlled' && upload
  const complete =
    ready &&
    alias &&
    !!picked &&
    permitted &&
    !!draft.service &&
    !!draft.category &&
    !!draft.reason &&
    (draft.period === 'undated' ||
      (draft.period === 'financial_year' && exactFinancialYear(draft.year.trim())))
  const checked = nonprivate === binding && owned === binding
  const options = draft.classification
    ? categoriesFor(draft.classification as SourceReviewSelection['classification'])
    : []
  const select = (
    label: string,
    name: 'alias' | 'classification' | 'service' | 'category' | 'period' | 'reason',
    choices: Array<[string, string]>
  ) => (
    <label className="block text-sm text-text-secondary space-y-1">
      <span>{label}</span>
      <select
        required
        aria-label={label}
        className="input input-bordered w-full"
        value={draft[name]}
        onChange={event =>
          update({
            [name]: event.target.value,
            ...(name === 'classification' ? { category: '' } : {}),
            ...(name === 'period' ? { year: '' } : {}),
          })
        }
      >
        <option value="">Choose {label.toLowerCase()}</option>
        {choices.map(([value, title]) => (
          <option key={value} value={value}>
            {title}
          </option>
        ))}
      </select>
    </label>
  )
  return (
    <form
      className="space-y-4"
      onSubmit={event => {
        event.preventDefault()
        setTouched(true)
        if (!complete || !checked || !alias || !picked || actions.busy || inFlight.current) return
        const selection: SourceReviewSelection = {
          source_alias_id: alias.source_alias_id,
          customer_type: alias.customer_type,
          customer_id: alias.customer_id,
          classification: draft.classification as SourceReviewSelection['classification'],
          service: draft.service as SourceReviewSelection['service'],
          category: draft.category,
          financial_year: draft.period === 'undated' ? null : draft.year.trim(),
          period_basis: draft.period as SourceReviewSelection['period_basis'],
          reason: draft.reason as SourceReviewSelection['reason'],
          confirm_nonprivate_source: true,
          confirm_client_ownership: true,
          terms_version: SOURCE_REVIEW_TERMS,
        }
        const payload = {
          source_document_id: picked.source_document_id,
          expected_source_snapshot_sha256: picked.source_snapshot_sha256,
          selection,
        }
        inFlight.current = true
        void actions.run(async () => {
          try {
            const result = await command<SourceReviewStatus>(reviewsPath(client), {
              ...payload,
              idempotency_key: actions.idempotencyKey({ client, ...payload }),
            })
            if (result.client_id !== client || !isUuid(result.review_id))
              throw new Error(
                'The proposal receipt could not be verified. Retry the same proposal.'
              )
            await onSaved(result)
          } finally {
            inFlight.current = false
          }
        })
      }}
    >
      <p className="text-sm text-text-secondary">
        Select the originating client record and the interpretation to send for independent client
        review.
      </p>
      {sources.isFetching && <p role="status">Checking current original documents…</p>}
      {sources.isError && <p role="alert">{sources.error.message}</p>}
      <button
        type="button"
        className="btn btn-secondary"
        disabled={actions.busy || sources.isFetching}
        onClick={() => {
          setNonprivate(null)
          setOwned(null)
          void Promise.all([
            queryClient.invalidateQueries({
              queryKey: [...accountKey, client, 'source-review-aliases'],
            }),
            queryClient.invalidateQueries({
              queryKey: [...accountKey, client, 'source-review-sources'],
            }),
          ])
        }}
      >
        Refresh documents and client records
      </button>
      <fieldset disabled={!sourcesReady || actions.busy} className="min-w-0 space-y-4">
        <label className="block text-sm text-text-secondary space-y-1">
          <span>Original firm document</span>
          <select
            aria-label="Original firm document"
            className="input input-bordered w-full"
            value={draft.source}
            required
            onChange={event => update({ source: event.target.value })}
          >
            <option value="">Choose an authorized original document</option>
            {sourcesReady &&
              sources.data?.items.map(item => (
                <option
                  key={item.source_document_id}
                  value={item.source_document_id}
                  disabled={!item.reviewable}
                >
                  {item.filename}
                  {item.status === 'requires_source_mapping'
                    ? ' — source mapping required'
                    : item.status === 'requires_content_review'
                      ? ' — content review required'
                      : ''}
                </option>
              ))}
          </select>
        </label>
        {sourcesReady && sources.data?.items.length === 0 && (
          <p className="text-sm text-text-secondary">
            No authorized original documents on this page.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={sourcePage === 1}
            onClick={() => {
              update({ source: '' })
              setSourcePage(sourcePage - 1)
            }}
          >
            Previous documents
          </button>
          <span>
            Page {sourcePage} · {sources.data?.total ?? 0} authorized documents
          </span>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={!sources.data || sourcePage * sources.data.page_size >= sources.data.total}
            onClick={() => {
              update({ source: '' })
              setSourcePage(sourcePage + 1)
            }}
          >
            Next documents
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {select(
            'Originating client record',
            'alias',
            ready
              ? aliases.map(row => [
                  row.source_alias_id,
                  `${row.customer_type === 'individual' ? 'Individual' : 'Business'} client record #${row.customer_id}`,
                ])
              : []
          )}
          {select('Document ownership', 'classification', [
            ...(upload ? [['client_controlled', 'Client-owned document'] as [string, string]] : []),
            ...(publish
              ? [['client_output', 'Verified professional output'] as [string, string]]
              : []),
          ])}
          {select('Document service', 'service', Object.entries(serviceLabels))}
          {select(
            'Portable document category',
            'category',
            options.map(value => [value, categoryLabels[value]])
          )}
          {select('Document period', 'period', [
            ['financial_year', 'Financial year'],
            ['undated', 'Undated — no financial year assigned'],
          ])}
          {draft.period === 'financial_year' && (
            <Field
              label="Actual financial year (YYYY-YY)"
              value={draft.year}
              required
              placeholder="YYYY-YY"
              onBlur={() => setTouched(true)}
              onChange={event => update({ year: event.target.value })}
            />
          )}
          {select('Review reason', 'reason', Object.entries(reviewReasons))}
        </div>
        {touched &&
          draft.period === 'financial_year' &&
          draft.year &&
          !exactFinancialYear(draft.year.trim()) && (
            <p role="alert" className="text-error text-sm">
              Use the document’s actual consecutive financial year, for example 2025-26.
            </p>
          )}
        <label className="flex items-start gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={nonprivate === binding}
            onChange={event => setNonprivate(event.target.checked ? binding : null)}
          />
          I reviewed the original source and confirm it is nonprivate and contains no confidential
          firm work.
        </label>
        <label className="flex items-start gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={owned === binding}
            onChange={event => setOwned(event.target.checked ? binding : null)}
          />
          I confirm this document belongs to the selected client and the proposed interpretation is
          accurate.
        </label>
        <button className="btn btn-primary" disabled={!complete || !checked || actions.busy}>
          Send interpretation for client review
        </button>
      </fieldset>
      {ready && aliases.length === 0 && (
        <p className="text-sm text-text-secondary">
          No current originating client records are available for a proposal.
        </p>
      )}
      <CommandStatus {...actions} />
    </form>
  )
}
